import { Component, Input, OnInit } from '@angular/core';
import {
	PropertyType,
	PropertyStatus,
	MatterPropertyType,
	RegistrationMethodType,
	PropertyUnitType,
	LandTransferTax,
	MatterFieldMappings,
	MatterFileType,
} from '@models/matter';
import { PropertyUsage } from '@models/title-insurance';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { select, Select } from '@ngxs/store';
import { MatterDispatch } from '@state/matter';
import { Observable } from 'rxjs';
import { cloneDeep } from 'lodash';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { fade, openClose } from '@core/animations/animations';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { AI_GENERATED_ICON, ICON } from '@core/util/icon';
import { showMultiList, showTenantedList } from '@core/util/property';
import { DirectionOfFeeType } from '@models/businessSettings/directionOfFees';
import { BusinessState } from '@state/business';
import { DirectionOfAmountSettings } from '@models/businessSettings/settings';
import { SnackbarService } from '@services/ui/snackbar.service';
import { TestHooks } from './property-type.hooks';
import {
	propertyUsageTypeItems,
	unitTypeItems,
	propertyStatusItems,
} from './property-type.models';

@UntilDestroy()
@Component({
	selector: 'app-property-type',
	templateUrl: './property-type.component.html',
	styleUrls: ['./property-type.component.scss'],
	animations: [fade(), openClose(300)],
	standalone: false,
})
export class PropertyTypeComponent implements OnInit {
	testHooks = TestHooks;
	public ICON = ICON;
	public AI_ICON = AI_GENERATED_ICON.icon;
	public AI_TOOLTIP = AI_GENERATED_ICON.tooltip;
	public AI_CLASSES = AI_GENERATED_ICON.classes;
	public propertyTypes = PropertyType;
	public propertyUsages = PropertyUsage;
	public propertyUsageTypes = propertyUsageTypeItems;
	matterFileType = MatterFileType;

	public unitTypes = unitTypeItems;

	public numberOfUnits = [
		{ label: '2', value: 2 },
		{ label: '3', value: 3 },
		{ label: '4', value: 4 },
		{ label: '5', value: 5 },
		{ label: '6', value: 6 },
	];

	public propertyStatus = propertyStatusItems;

	public _propertyType: MatterPropertyType;
	private _ltt: LandTransferTax;

	private _showConditionList = [
		PropertyType.HOME,
		PropertyType.CONDO,
		PropertyType.OTHER,
		PropertyType.BUILDING,
	];
	private _showOccupancyList = [
		PropertyType.HOME,
		PropertyType.BUILDING,
		PropertyType.CONDO,
		PropertyType.FARM_LAND,
		PropertyType.MOBILE_HOME,
		PropertyType.OTHER,
	];

	@Select(SelectedMatter.propertyType)
	propertyType$: Observable<MatterPropertyType>;
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(BusinessState.getDirectionOfAmount)
	settings: DirectionOfAmountSettings;
	@Select(SelectedMatter.landTransferTax) ltt$: Observable<LandTransferTax>;
	@ViewSelectSnapshot(SelectedMatter.ownerResidesAtProperty)
	ownerResidesAtProperty: RegistrationMethodType;
	@ViewSelectSnapshot(SelectedMatter.ownerResidesAtPropertyMoveoutDate)
	ownerResidesAtPropertyMoveoutDate: string;
	@Select(SelectedMatter.getFieldMappings)
	fieldMappings$: Observable<MatterFieldMappings>;
	fileType_ = select(SelectedMatter.matterFileType);

	@Input() showCondoPlanOverview = true;

	constructor(
		private matterDispatch: MatterDispatch,
		private snackbarService: SnackbarService,
	) {}

	ngOnInit(): void {
		this.propertyType$.pipe(untilDestroyed(this)).subscribe((propertyType) => {
			if (propertyType) {
				this._propertyType = cloneDeep(propertyType);
			} else {
				this._propertyType = {};
			}
		});
		this.ltt$.pipe(untilDestroyed(this)).subscribe((ltt) => {
			this._ltt = ltt;
		});
	}

	get showTenanted() {
		return this.getPropertyTypeExists(
			this._propertyType?.type,
			showTenantedList,
		);
	}

	get showCondition() {
		return this.getPropertyTypeExists(
			this._propertyType?.type,
			this._showConditionList,
		);
	}

	get showMultiUnit() {
		return this.getPropertyTypeExists(this._propertyType?.type, showMultiList);
	}

	get showOccupancy() {
		return this.getPropertyTypeExists(
			this._propertyType?.type,
			this._showOccupancyList,
		);
	}

	getPropertyTypeExists(propertyType: PropertyType, list: PropertyType[]) {
		return propertyType && list.find((x) => x === propertyType);
	}

	propertyUsageUpdated($event: PropertyUsage) {
		const updated: Partial<MatterPropertyType> = { usage: $event };
		if (
			updated.usage === PropertyUsage.COMMERCIAL &&
			this._propertyType.type !== PropertyType.BUILDING &&
			this._propertyType.type !== PropertyType.OTHER &&
			this._propertyType.type !== PropertyType.CONDO &&
			this._propertyType.type !== PropertyType.FARM_LAND &&
			this._propertyType.type !== PropertyType.VACANT
		) {
			updated.type = PropertyType.BUILDING;
		} else if (this._propertyType.type === PropertyType.BUILDING) {
			updated.type = PropertyType.HOME;
		}
		if (this._propertyType.type === PropertyType.MOBILE_HOME) {
			this.snackbarService.openSnackBar({
				text: 'We\'ve reverted LTT "display in" to default settings',
				type: 'info',
				icon: ICON.INFO,
			});
			this.matterDispatch.UpdateLtt(this.matterId, {
				displayIn: this.settings.location,
				hideFromTL: false,
			});
		}

		this.matterDispatch.SetPropertyType(this.matterId, updated);
	}

	propertyOtherUpdated($event: string) {
		this.matterDispatch.SetPropertyType(this.matterId, { otherType: $event });
	}

	onPropertyTypeChange($event: MatterPropertyType) {
		if (
			$event.type === PropertyType.MOBILE_HOME &&
			$event.usage === PropertyUsage.RESIDENTIAL &&
			this._ltt?.displayIn !== DirectionOfFeeType.NONE
		) {
			this.snackbarService.openSnackBar({
				text: "We've hidden the LTT and MLTT amounts as they're not applicable for mobile homes",
				type: 'info',
				icon: ICON.INFO,
			});
			this.matterDispatch.UpdateLtt(this.matterId, {
				displayIn: DirectionOfFeeType.NONE,
				hideFromTL: true,
			});
		} else if (
			this._propertyType.type === PropertyType.MOBILE_HOME &&
			$event.type !== PropertyType.MOBILE_HOME
		) {
			this.snackbarService.openSnackBar({
				text: 'We\'ve reverted LTT "display in" to default settings',
				type: 'info',
				icon: ICON.INFO,
			});
			this.matterDispatch.UpdateLtt(this.matterId, {
				displayIn: this.settings.location,
				hideFromTL: false,
			});
		}
		this.matterDispatch.SetPropertyType(this.matterId, $event);
	}

	propertyStatusUpdated($event: PropertyStatus) {
		if ($event === PropertyStatus.RESALE) {
			this.matterDispatch.OverwriteFields('occupancyDate', null);
		}
		this.matterDispatch.SetPropertyType(this.matterId, { status: $event });
	}

	propertyTenantedUpdated($event) {
		this.matterDispatch.SetPropertyType(this.matterId, { tenanted: $event });
	}

	propertyStatusParcelUpdated($event) {
		const updated: Partial<MatterPropertyType> = { parcelOfTiedLand: $event };

		this.matterDispatch.SetPropertyType(this.matterId, updated);
	}

	setOwnerResidesInSubjectProperty($event) {
		if (!$event) {
			this.matterDispatch.SetOwnerResidesAtPropertyMoveoutDate(
				this.matterId,
				'',
			);
		}

		this.matterDispatch.SetOwnerResidesAtProperty(this.matterId, $event);
	}

	propertyMultiUnitUpdated($event) {
		const updated: Partial<MatterPropertyType> = { multiUnit: $event };
		updated.numberOfUnits = $event ? 2 : null;
		updated.unitType = $event ? PropertyUnitType.MULTI_FAMILY : null;

		this.matterDispatch.SetPropertyType(this.matterId, updated);
	}

	setUnitType($event) {
		this.matterDispatch.SetPropertyType(this.matterId, { unitType: $event });
	}

	setUnitNumber($event) {
		this.matterDispatch.SetPropertyType(this.matterId, {
			numberOfUnits: $event,
		});
	}

	condoPlanChange($event) {
		this.matterDispatch.SetPropertyType(this.matterId, { condoPlan: $event });
	}

	percentageInterestChange($event) {
		this.matterDispatch.SetPropertyType(this.matterId, {
			percentageInterest: $event,
		});
	}

	commonExpensesChange($event) {
		this.matterDispatch.SetPropertyType(this.matterId, {
			commonExpenses: $event,
		});
	}

	setOwnerResidesAtPropertyMoveoutDate(event: string) {
		this.matterDispatch.SetOwnerResidesAtPropertyMoveoutDate(
			this.matterId,
			event,
		);
	}
}
